import { supabase } from '../lib/supabase'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

/** Error thrown by uploadReceipt for a validation failure. `code` identifies the rule. */
export class ReceiptUploadError extends Error {
  constructor(code) {
    super(code)
    this.name = 'ReceiptUploadError'
    this.code = code
  }
}

/**
 * Validate + upload a receipt file to the `receipts` bucket and insert a
 * payment_receipts row (status 'Pending Review'). Returns the inserted row.
 *
 * @param {object} args
 * @param {File}   args.file
 * @param {string} args.registrationId
 * @param {string} args.campId
 * @param {string} args.userId          - id stored on the row (attendee for admin uploads)
 * @param {string|number} args.amount
 * @param {string} args.paymentMethod
 * @returns {Promise<object>} the inserted payment_receipts row
 */
export async function uploadReceipt({ file, registrationId, campId, userId, amount, paymentMethod }) {
  if (!file) throw new ReceiptUploadError('NO_FILE')
  if (file.size > MAX_FILE_SIZE) throw new ReceiptUploadError('FILE_TOO_LARGE')
  const parsedAmount = parseFloat(amount)
  if (!amount || Number.isNaN(parsedAmount) || parsedAmount <= 0) throw new ReceiptUploadError('INVALID_AMOUNT')
  if (!paymentMethod) throw new ReceiptUploadError('NO_METHOD')

  const ext = file.name.split('.').pop().toLowerCase()
  const timestamp = Date.now()
  const path = `${campId}/${registrationId}/${timestamp}.${ext}`

  const { error: uploadErr } = await supabase.storage
    .from('receipts')
    .upload(path, file, { contentType: file.type })
  if (uploadErr) throw uploadErr

  const { data: { publicUrl } } = supabase.storage.from('receipts').getPublicUrl(path)

  const { data, error: insertErr } = await supabase
    .from('payment_receipts')
    .insert({
      registration_id: registrationId,
      camp_id:         campId,
      user_id:         userId,
      receipt_url:     publicUrl,
      amount:          parsedAmount,
      payment_method:  paymentMethod,
      status:          'Pending Review',
    })
    .select()
    .single()
  if (insertErr) throw insertErr

  return data
}
